import { Injectable } from '@angular/core';
import { GeoPosition } from 'geo-position.ts/lib';
import { combineLatest, fromEventPattern, Observable, of, throwError, } from 'rxjs';
import { map, shareReplay, startWith, throttleTime } from 'rxjs/operators';
import { ICoordinatesLike } from '../../../models/coordinate-like.model.i';
import { INavigationInfo } from '../../../models/navigation-info.model.i';
import { BrowserUtils } from '../utils/browser.utils';
import { NavigationUtils, SimpleOrientationType } from '../utils/navigation.utils';

interface ExtendedDeviceOrientationEvent extends DeviceOrientationEvent {
    webkitCompassHeading: number;
}

@Injectable({
    providedIn: 'root'
})
export class NavigationService {

    private readonly _defaultOrientationType: SimpleOrientationType = NavigationUtils.getDefaultScreenOrientationType();
    private position$: Observable<Coordinates>;

    static _bearing(lat1: number, lng1: number, lat2: number, lng2: number) {
        lat1 = NavigationService._toRad(lat1);
        lng1 = NavigationService._toRad(lng1);
        lat2 = NavigationService._toRad(lat2);
        lng2 = NavigationService._toRad(lng2);

        const dLon = (lng2 - lng1);
        const y = Math.sin(dLon) * Math.cos(lat2);
        const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
        const rad = Math.atan2(y, x);
        const bearing = NavigationService._toDeg(rad);
        return bearing; // 360 - ((bearing + 360) % 360);
    }

    static _toDeg(rad) {
        return rad * 180 / Math.PI;
    }

    static _toRad(deg) {
        return deg * Math.PI / 180;
    }

    constructor() {
        const geolocation = navigator.geolocation;

        this.position$ = geolocation ? new Observable<Coordinates>(subscriber => {
            const watchId = geolocation.watchPosition(
                position => {
                    subscriber.next(position ? position.coords : void 0);
                }, error => {
                    subscriber.error(error);
                }, {enableHighAccuracy: true});

            return () => {
                geolocation.clearWatch(watchId);
                subscriber.complete();
            };

        }).pipe(
            shareReplay()
        ) : throwError('No geolocation API found');
    }

    // get position from current device
    getCurrentPosition(): Observable<Coordinates> {
        return this.position$;
    }

    getNavigationInfo(target: ICoordinatesLike): Observable<INavigationInfo> {
        return NavigationUtils.getCurrentNavigationInfo(target, this._defaultOrientationType);
    }


    // get direction/bearing from block
    getNavigationInfo2(target: { latitude: number, longitude: number }): Observable<{ direction: number, distance: number }> {
        if (!target) {
            return of({direction: 0, distance: 0});
        }

        return combineLatest(
            this.getCurrentPosition(),
            fromEventPattern<DeviceOrientationEvent>(
                handler => window.addEventListener(BrowserUtils.isChrome ? 'deviceorientationabsolute' : 'deviceorientation', handler),
                handler => window.removeEventListener(BrowserUtils.isChrome ? 'deviceorientationabsolute' : 'deviceorientation', handler)
            ).pipe(startWith({alpha: 0, beta: 0, gamma: 0})),
            fromEventPattern<DeviceOrientationEvent>(
                handler => window.addEventListener(BrowserUtils.isChrome ? 'deviceorientation' : 'deviceorientationabsolute', handler),
                handler => window.removeEventListener(BrowserUtils.isChrome ? 'deviceorientation' : 'deviceorientationabsolute', handler)
            ).pipe(startWith({alpha: 0, beta: 0, gamma: 0}))
        ).pipe(
            throttleTime(500),
            map(([coordinates, orientation, orientation2]) => {
                return {
                    ...this.calculateDirection(coordinates, orientation, target, orientation2),
                    distance: this.calculateDistance(coordinates, target)
                };
            })
        );
    }

    calculateDirection(coordinates, orientation, target, orientation2) {
        const currentDirectionInDegrees = this.compassHeading(orientation);
        const directionToTargetInDegrees = NavigationService._bearing(coordinates.latitude, coordinates.longitude, target.latitude, target.longitude);

        const result = directionToTargetInDegrees + currentDirectionInDegrees;

        return {
            direction: result,
            north: currentDirectionInDegrees
        };
    }

    compassHeading(orientation: ExtendedDeviceOrientationEvent) {

        const webkitCompassHeading = orientation.webkitCompassHeading;
        const orientationAlpha = orientation.alpha;
        const calculatedHeading = this.calculateHeading(orientation);

        if (orientation.absolute && orientation.webkitCompassHeading != null) {
            return 360 - webkitCompassHeading;
        } else if (BrowserUtils.isChrome) {
            return orientationAlpha;
        }

        return calculatedHeading;
    }

    calculateHeading(orientation: ExtendedDeviceOrientationEvent) {

        const {alpha, beta, gamma} = orientation;
        // Convert degrees to radians
        const alphaRad = alpha * (Math.PI / 180);
        const betaRad = beta * (Math.PI / 180);
        const gammaRad = gamma * (Math.PI / 180);

        // Calculate equation components
        const cA = Math.cos(alphaRad);
        const sA = Math.sin(alphaRad);
        const cB = Math.cos(betaRad);
        const sB = Math.sin(betaRad);
        const cG = Math.cos(gammaRad);
        const sG = Math.sin(gammaRad);

        // Calculate A, B, C rotation components
        const rA = -cA * sG - sA * sB * cG;
        const rB = -sA * sG + cA * sB * cG;
        const rC = -cB * cG;

        // Calculate compass heading
        let compassHeading = Math.atan2(rA, rB);

        // Convert from half unit circle to whole unit circle
        if (rB < 0) {
            compassHeading += Math.PI;
        } else if (rA < 0) {
            compassHeading += 2 * Math.PI;
        }

        // Convert radians to degrees
        compassHeading *= 180 / Math.PI;

        return compassHeading;
    }


    calculateDistance(coordinates, target) {
        const position = new GeoPosition(coordinates.latitude, coordinates.longitude);
        const targetPosition = new GeoPosition(target.latitude, target.longitude);

        return position.Distance(targetPosition);
    }

}
